import { FC, ReactNode } from 'react';
import clsx from 'clsx';
import { cva, VariantProps } from 'class-variance-authority';
import { SvgInfo as Info } from '../svgs/Info';
import { Button } from '../Button';

const alertCVA = cva(
  [
    'flex items-center gap-[--padding-sm] p-[--padding-sm]',
    'rounded-[--corner-sm] border border-solid',
    'shadow-[0px_4px_32px_0px_rgba(18,18,18,0.05)]',
    'w-full',
  ],
  {
    variants: {
      state: {
        default:
          'bg-[--alert-default-bg] border-[--alert-default-border] text-[--alert-default-fg]',
        success:
          'bg-[--alert-success-bg] border-[--alert-success-border] text-[--alert-success-fg]',
        error: 'bg-[--alert-error-bg] border-[--alert-error-border] text-[--alert-error-fg]',
      },
    },
    defaultVariants: { state: 'default' },
  }
);

// Maps alert state to Button color prop
const actionColorMap = {
  default: 'blue',
  success: 'main',
  error: 'white',
} as const;

export interface AlertProps extends VariantProps<typeof alertCVA> {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: { label: string; onClick: () => void };
  className?: string;
}

export const Alert: FC<AlertProps> = ({
  state = 'default',
  title,
  description,
  icon,
  action,
  className,
}) => {
  const resolvedIcon = icon !== undefined ? icon : <Info className="shrink-0 size-[20px]" />;
  const actionColor = actionColorMap[state ?? 'default'];

  return (
    <div className={clsx(alertCVA({ state }), className)}>
      {resolvedIcon}

      <div className="flex min-w-0 flex-1 flex-col gap-[--padding-2xs]">
        <p className="font-[--font-primary] text-[14px] font-bold leading-[18px] tracking-[--spacing-letters-zero]">
          {title}
        </p>
        {description && (
          <p className="font-[--font-primary] text-[12px] font-normal leading-[16px] tracking-[--spacing-letters-zero]">
            {description}
          </p>
        )}
      </div>

      {action && (
        <Button
          variant="outlined"
          color={actionColor}
          size="small"
          onClick={action.onClick}
          className="!px-[--padding-sm] !py-[10px] !rounded-[--corner-xs]"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};
